import { useEffect, useState } from 'react';
import {
  Calendar,
  MapPin,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
} from 'lucide-react';
import { useAthleteAuth } from '@/contexts/AthleteAuthContext';
import { api } from '@/hooks/useApi';

interface EventDetail {
  id: string;
  name: string;
  nameEn?: string;
  startDate: string;
  endDate: string;
  location: string;
  locationEn?: string;
  address?: string;
  mapUrl?: string;
  description?: string;
  rules?: string;
  schedule?: ScheduleItem[];
}

interface ScheduleItem {
  id: string;
  day: number;
  date: string;
  startTime: string;
  endTime: string;
  title: string;
  titleEn?: string;
  venue: string;
  isMyCompetition?: boolean;
}

export default function AthleteEventPage() {
  const { token } = useAthleteAuth();
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;

    api
      .get('/me/event', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data?.data ?? res.data;
        setEvent(data);
      })
      .catch(() => {
        // Failed to fetch event info
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-20">
        <Calendar className="w-12 h-12 text-[var(--text-secondary)] mx-auto mb-4" />
        <h2 className="text-lg font-bold text-[var(--text-primary)]">暫無賽事資訊</h2>
        <p className="text-sm text-[var(--text-secondary)] mt-1">No event information available.</p>
      </div>
    );
  }

  // Group schedule by day
  const scheduleByDay = (event.schedule ?? []).reduce<Record<number, ScheduleItem[]>>(
    (acc, item) => {
      if (!acc[item.day]) acc[item.day] = [];
      acc[item.day].push(item);
      return acc;
    },
    {},
  );

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">賽事資訊</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">Event Information</p>
      </div>

      {/* Event Overview Card */}
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-6 shadow-sm mb-6">
        <h2 className="text-xl font-bold text-[var(--text-primary)] mb-1">{event.name}</h2>
        {event.nameEn && (
          <p className="text-sm text-[var(--text-secondary)] mb-4">{event.nameEn}</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-[var(--primary)] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">活動日期 / Dates</p>
              <p className="text-sm text-[var(--text-secondary)]">
                {event.startDate} ~ {event.endDate}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-[var(--primary)] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">地點 / Location</p>
              <p className="text-sm text-[var(--text-secondary)]">{event.location}</p>
              {event.locationEn && (
                <p className="text-xs text-[var(--text-secondary)]">{event.locationEn}</p>
              )}
              {event.address && (
                <p className="text-xs text-[var(--text-secondary)] mt-0.5">{event.address}</p>
              )}
            </div>
          </div>
        </div>

        {event.mapUrl && (
          <a
            href={event.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg border border-[var(--border)] text-sm text-[var(--primary)] hover:bg-[var(--bg-secondary)] transition-colors"
          >
            <MapPin className="w-4 h-4" />
            查看地圖 / View Map
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Description */}
      {event.description && (
        <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-6 shadow-sm mb-6">
          <h3 className="text-lg font-bold text-[var(--text-primary)] mb-3">
            活動說明 <span className="text-xs text-[var(--text-secondary)] font-normal">/ Description</span>
          </h3>
          <div className="prose prose-sm max-w-none text-[var(--text-secondary)]">
            <p className="whitespace-pre-wrap">{event.description}</p>
          </div>
        </div>
      )}

      {/* Schedule (filtered to athlete's competitions) */}
      {Object.keys(scheduleByDay).length > 0 && (
        <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-6 shadow-sm mb-6">
          <h3 className="text-lg font-bold text-[var(--text-primary)] mb-4">
            我的賽程{' '}
            <span className="text-xs text-[var(--text-secondary)] font-normal">/ My Schedule</span>
          </h3>
          <div className="space-y-6">
            {Object.entries(scheduleByDay)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([day, items]) => (
                <div key={day}>
                  <h4 className="text-sm font-semibold text-[var(--primary)] mb-2">
                    第 {day} 天 / Day {day}
                    {items[0]?.date && (
                      <span className="text-[var(--text-secondary)] font-normal ml-2">
                        ({items[0].date})
                      </span>
                    )}
                  </h4>
                  <div className="space-y-2">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className={`flex items-start gap-3 px-4 py-3 rounded-lg border text-sm ${
                          item.isMyCompetition
                            ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                            : 'border-[var(--border)] bg-[var(--bg-secondary)]'
                        }`}
                      >
                        <Clock className="w-4 h-4 text-[var(--text-secondary)] mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-[var(--text-primary)]">
                              {item.startTime} - {item.endTime}
                            </span>
                            {item.isMyCompetition && (
                              <span className="px-2 py-0.5 rounded-full bg-[var(--primary)] text-white text-xs">
                                我的項目 / My Event
                              </span>
                            )}
                          </div>
                          <p className="text-[var(--text-primary)] mt-0.5">{item.title}</p>
                          {item.titleEn && (
                            <p className="text-xs text-[var(--text-secondary)]">{item.titleEn}</p>
                          )}
                          <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {item.venue}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Rules */}
      {event.rules && (
        <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-5 h-5 text-[var(--primary)]" />
            <h3 className="text-lg font-bold text-[var(--text-primary)]">
              競賽規則{' '}
              <span className="text-xs text-[var(--text-secondary)] font-normal">
                / Rules & Regulations
              </span>
            </h3>
          </div>
          <div className="prose prose-sm max-w-none text-[var(--text-secondary)]">
            <p className="whitespace-pre-wrap">{event.rules}</p>
          </div>
        </div>
      )}
    </div>
  );
}
